import { InputAdornment, Stack } from "@mui/material";
import { Input } from "components";
import {
  useCreateDiscount,
  useUpdateDiscount,
} from "controller/hooks/configurations/billingAndContracts/discount";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import CustomQuillEditor from "components/quill-custom/QuillEditor";
import Decimal from "decimal.js";
import { toast } from "react-hot-toast";

const schema = yup.object({
  discount_name: yup.string().required("Discount Name is required"),
  discount_description: yup.string().nullable(),
  is_percentage: yup.boolean().required("Discount Type is required"),
  discount_rate: yup
    .number()
    .required("Discount Rate is required")
    .min(0.1, "Discount Rate must be greater than 0")
    .max(100, "Discount Rate must be less than 100")
    .typeError("Discount Rate must be a number"),
});

interface AddNewDiscountProps {
  selectedDiscount?: any;
  onSubmit: (data: any) => void;
  closeDrawer?: () => void;
}

export const AddNewDiscountContent = forwardRef(
  ({ selectedDiscount, onSubmit, closeDrawer }: AddNewDiscountProps, ref) => {
    const { mutateAsync: addDiscount, isLoading: isCreateDiscountLoading } =
      useCreateDiscount();
    const { mutateAsync: updateDiscount, isLoading: isUpdateDiscountLoading } =
      useUpdateDiscount();
    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        discount_name: "",
        discount_description: "",
        is_percentage: true,
        discount_rate: 0,
      },
    });

    useEffect(() => {
      if (selectedDiscount) {
        reset({
          discount_name: selectedDiscount.discount_name,
          discount_description: selectedDiscount.discount_description,
          is_percentage: selectedDiscount.is_percentage ?? true,
          discount_rate: selectedDiscount.discount_rate ?? 0,
        });
      } else {
        reset({
          discount_name: "",
          discount_description: "",
          is_percentage: true,
          discount_rate: 0,
        });
      }
    }, [selectedDiscount]);

    const onSubmitHandler = handleSubmit(async (data: any) => {
      if (selectedDiscount?.mapping_id) {
        updateDiscountHandler({
          ...data,
          discount_id: selectedDiscount.mapping_id,
        });
      } else {
        addDiscountHandler(data);
      }
    });

    const addDiscountHandler = async (data: any) => {
      try {
        const resp = await addDiscount(data);
        onSubmit(resp);
      } catch (error) {
        console.log(error);
        toast.error((error as any)?.message || "Unable to add discount!");
      }
    };

    const updateDiscountHandler = async (data: any) => {
      try {
        const resp = await updateDiscount(data);
        onSubmit(resp);
      } catch (error) {
        console.log(error);
        toast.error((error as any)?.message || "Unable to update discount!");
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit: onSubmitHandler,
      isLoading: () => isCreateDiscountLoading || isUpdateDiscountLoading,
      cancelButtonClick: () => {
        if (!closeDrawer) return;
        closeDrawer();
        // reset();
      },
    }));

    return (
      <Stack gap="16px" p="16px">
        <Input
          control={control}
          name="discount_name"
          label="Discount Name"
          required={true}
          error={!!errors.discount_name}
          errorMessage={errors.discount_name?.message}
        />
        {/* <Input
          control={control}
          name="is_percentage"
          label="Discount Type"
          error={!!errors.is_percentage}
          errorMessage={errors.is_percentage?.message}
        /> */}
        <Controller
          control={control}
          name="discount_rate"
          render={({ field: { onChange, value, ...rest } }) => {
            return (
              <Input
                {...rest}
                value={value}
                required={true}
                digitsAfterDecimal={2}
                onChange={(e) => {
                  if (
                    e.target.value &&
                    new Decimal(e.target.value).toNumber() > 100
                  ) {
                    e.target.value = "100";
                    onChange(e);
                  }
                  onChange(e);
                }}
                onBlur={(e) => {
                  if (
                    e.target.value &&
                    new Decimal(e.target.value).toNumber() > 100
                  ) {
                    e.target.value = "100";
                    onChange(e);
                  }
                }}
                label="Discount"
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "+" ||
                    e.key === "-"
                  ) {
                    e.preventDefault();
                  }
                }}
                error={!!errors.discount_rate}
                errorMessage={errors.discount_rate?.message}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="discount_description"
          render={({ field }) => (
            <CustomQuillEditor
              name="discount_description"
              placeholder="Description"
              value={field.value}
              onChange={({ value }) => {
                field.onChange(value);
              }}
              showToolbar={false}
              error={!!errors.discount_description}
              errorMessage={errors.discount_description?.message}
            />
          )}
        />
      </Stack>
    );
  },
);
